import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  LocalPermissions,
  Roles,
  RolesListData,
  RolesListParams,
  RolesToPermissions,
  RolesToPermissionsListData,
  RoleToPermissionsCreateResponse,
  RoleToPermissionsDeletePayload,
  RoleToPermissionsPayload,
} from '@/libs/interfaces'
import { rolesApiMock } from '@/libs/mocks/handlers/roles'

const rolesApiReal = {
  create: async (body: Partial<Roles>): Promise<CreateApiResult<Partial<Roles>>> => {
    return await api
      .post<ApiResponse<Partial<Roles>>>(URLs.Roles.create(body.orgID || ''), body)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  delete: async (payload: Partial<Roles>) => {
    return await api
      .delete(URLs.Roles.byID(payload.orgID || '', payload.roleID || ''))
      .then((response) => response.data)
  },
  getByID: async (params: Partial<Roles>): Promise<Partial<Roles>> => {
    return await api
      .get<ApiResponse<Partial<Roles>>>(URLs.Roles.byID(params.orgID || '', params.roleID || ''))
      .then((response) => response.data.data)
  },
  list: async (params: RolesListParams): Promise<Roles[]> => {
    const query = buildQueryParams(params)
    return await api
      .get<ApiResponse<RolesListData>>(URLs.Roles.list(params.orgID || '', query))
      .then((response) => response?.data?.data?.roles || [])
  },
  update: async (payload: Partial<Roles>) => {
    return await api
      .patch(URLs.Roles.byID(payload.orgID || '', payload.roleID || ''), payload)
      .then((response) => response.data)
  },
  createRolesToPermissions: async (
    params: Partial<RolesToPermissions>,
    payload: RoleToPermissionsPayload,
  ): Promise<RolesToPermissions[]> => {
    return await api
      .post<RoleToPermissionsCreateResponse>(
        URLs.Roles.roleToPermissions(params.orgID || ''),
        payload,
      )
      .then((response) => response.data.roleToPerms)
  },
  getRolesToPermissions: async (params: Partial<Roles>): Promise<LocalPermissions[]> => {
    return await api
      .get<ApiResponse<RolesToPermissionsListData>>(
        URLs.Roles.roleWithPermissions(params.orgID || '', params.roleID || ''),
      )
      .then((response) => response?.data?.data?.allPermissionsWithStatus || [])
  },
  deleteRolesToPermissions: async (params: RoleToPermissionsDeletePayload): Promise<void> => {
    await Promise.all(
      params.permissionIDs.map((permissionID) =>
        api.delete(URLs.Roles.roleToPermission(params.orgID, params.roleID, permissionID)),
      ),
    )
  },
}

export const rolesApi: typeof rolesApiReal = __IS_MOCK_MODE__ ? rolesApiMock : rolesApiReal
